import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Snackbar,
    Typography
} from '@mui/material';
import {Check, Cancel, Schedule, Upgrade} from '@mui/icons-material';
import {SubscriptionAPI} from '../services/API/SubscriptionAPI';
import {PlanDTO, SubscriptionDTO} from '../types/DTOs/SubscriptionDTO';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';

const PLAN_ORDER = ['free', 'basic', 'premium'];

const SubscriptionPlan: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {isAuthenticated} = useAuth();
    const [subscription, setSubscription] = useState<SubscriptionDTO | null>(null);
    const [plans, setPlans] = useState<PlanDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
    const [pendingDowngradePlan, setPendingDowngradePlan] = useState<PlanDTO | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const plansData = await SubscriptionAPI.Instance<SubscriptionAPI>().getPlans();
            setPlans(plansData);

            if (isAuthenticated) {
                try {
                    const subData = await SubscriptionAPI.Instance<SubscriptionAPI>().getSubscription();
                    setSubscription(subData);
                } catch (err: any) {
                    if (err?.response?.status === 404) {
                        setSubscription({
                            id: '',
                            planType: 'free',
                            status: 'active',
                            startDate: null,
                            endDate: null,
                            paymentMethod: null,
                            nextBillingDate: null,
                            pendingPlanType: null,
                        });
                    } else if (err?.response?.status === 401) {
                        setSubscription(null);
                    } else {
                        setError(t('subscription.errorLoad'));
                    }
                }
            }
        } catch (err: any) {
            setError(t('subscription.errorLoad'));
        } finally {
            setLoading(false);
        }
    };

    const handleChoosePlan = async (plan: PlanDTO) => {
        if (!subscription) return;

        const currentIndex = PLAN_ORDER.indexOf(subscription.planType);
        const targetIndex = PLAN_ORDER.indexOf(plan.planType);

        if (plan.planType === subscription.planType) return;

        if (targetIndex > currentIndex) {
            if (subscription.planType === 'free' || (subscription.planType === 'basic' && plan.planType === 'premium')) {
                await handleCheckout(plan);
            } else {
                await handleUpgrade(plan);
            }
        } else {
            setPendingDowngradePlan(plan);
            setDowngradeDialogOpen(true);
        }
    };

    const handleCheckout = async (plan: PlanDTO) => {
        try {
            setActionLoading(true);
            const response = await SubscriptionAPI.Instance<SubscriptionAPI>().createCheckout(plan.planType);
            window.location.href = response.approvalUrl;
        } catch (err: any) {
            setError(err?.response?.data?.message || t('subscription.errorCheckout'));
            setActionLoading(false);
        }
    };

    const handleUpgrade = async (plan: PlanDTO) => {
        try {
            setActionLoading(true);
            const response = await SubscriptionAPI.Instance<SubscriptionAPI>().upgradePlan(plan.planType);
            setSuccess(response.message);
            await loadData();
        } catch (err: any) {
            setError(err?.response?.data?.message || t('subscription.errorUpgrade'));
        } finally {
            setActionLoading(false);
        }
    };

    const handlePlanAction = (plan: PlanDTO) => {
        if (!isAuthenticated) {
            navigate('/register');
            return;
        }
        if (subscription) {
            handleChoosePlan(plan);
        } else {
            handleCheckout(plan);
        }
    };

    const handleConfirmDowngrade = async () => {
        if (!pendingDowngradePlan) return;
        setDowngradeDialogOpen(false);
        await handleUpgrade(pendingDowngradePlan);
        setPendingDowngradePlan(null);
    };

    const handleCancelSubscription = async () => {
        try {
            setActionLoading(true);
            setCancelDialogOpen(false);
            await SubscriptionAPI.Instance<SubscriptionAPI>().cancelSubscription();
            setSuccess(t('subscription.cancelSuccess'));
            await loadData();
        } catch (err: any) {
            setError(err?.response?.data?.message || t('subscription.errorCancel'));
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const isCurrentPlan = (planType: string) => subscription?.planType === planType;
    const isPendingPlan = (planType: string) => subscription?.pendingPlanType === planType;
    const isUpgrade = (planType: string) => {
        if (!subscription) return false;
        return PLAN_ORDER.indexOf(planType) > PLAN_ORDER.indexOf(subscription.planType);
    };

    const getFeaturesForPlan = (planType: string): string[] => {
        const featureMap: Record<string, string[]> = {
            free: [
                t('subscription.featureCvSections'),
                t('subscription.featurePdfExport'),
                t('subscription.feature1ShareTemplate'),
            ],
            basic: [
                t('subscription.featureCvSections'),
                t('subscription.featurePdfExport'),
                t('subscription.featureUnlimitedShares'),
                t('subscription.featureNoAds'),
            ],
            premium: [
                t('subscription.featureCvSections'),
                t('subscription.featurePdfExport'),
                t('subscription.featureUnlimitedShares'),
                t('subscription.featureNoAds'),
                t('subscription.featureQrShare'),
                t('subscription.featureCvTranslation'),
            ],
        };
        return featureMap[planType] || [];
    };

    const getDowngradeFeatures = (): string[] => {
        if (!pendingDowngradePlan || !subscription) return [];
        const currentFeatures = getFeaturesForPlan(subscription.planType);
        const targetFeatures = getFeaturesForPlan(pendingDowngradePlan.planType);
        return currentFeatures.filter(f => !targetFeatures.includes(f));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" sx={{py: 8}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Box sx={{mb: 8}}>
            <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
                {t('subscription.title')}
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{mb: 4}}>
                {t('subscription.subtitle')}
            </Typography>

            {/* Current Subscription Info */}
            {subscription && isAuthenticated && (
                <Card sx={{mb: 4, maxWidth: 600, mx: 'auto'}}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('subscription.currentPlan')}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                            <Chip
                                label={subscription.planType.toUpperCase()}
                                color={subscription.planType === 'premium' ? 'secondary' : 'primary'}
                            />
                            <Chip
                                label={subscription.status}
                                color={subscription.status === 'active' ? 'success' : 'warning'}
                                variant="outlined"
                            />
                        </Box>
                        {subscription.startDate && (
                            <Typography variant="body2" color="text.secondary">
                                {t('subscription.startDate')}: {formatDate(subscription.startDate)}
                            </Typography>
                        )}
                        {subscription.nextBillingDate && (
                            <Typography variant="body2" color="text.secondary" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Schedule fontSize="small"/>
                                {t('subscription.nextBilling')}: {formatDate(subscription.nextBillingDate)}
                            </Typography>
                        )}
                        {subscription.endDate && (
                            <Typography variant="body2" color="text.secondary">
                                {t('subscription.endDate')}: {formatDate(subscription.endDate)}
                            </Typography>
                        )}
                        {subscription.pendingPlanType && (
                            <Alert severity="info" sx={{mt: 2}}>
                                <strong>{t('subscription.pendingPlan')}:</strong> {subscription.pendingPlanType.toUpperCase()} - {t('subscription.effectiveAtNextBilling')}
                            </Alert>
                        )}
                    </CardContent>
                    {subscription.planType !== 'free' && (
                        <CardActions sx={{px: 2, pb: 2}}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Cancel/>}
                                onClick={() => setCancelDialogOpen(true)}
                                disabled={actionLoading}
                            >
                                {t('subscription.cancelSubscription')}
                            </Button>
                        </CardActions>
                    )}
                </Card>
            )}

            {/* Plans Grid */}
            <Grid container spacing={4} justifyContent="center">
                {plans.map((plan) => (
                    <Grid size={{xs: 12, md: 4}} key={plan.planType}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                p: 3,
                                border: 2,
                                borderColor: isCurrentPlan(plan.planType) ? 'success.main' : 'primary.main',
                                position: 'relative',
                            }}
                        >
                            {isCurrentPlan(plan.planType) && (
                                <Chip
                                    label={t('subscription.current')}
                                    color="success"
                                    sx={{position: 'absolute', top: 12, right: 12}}
                                />
                            )}
                            {isPendingPlan(plan.planType) && (
                                <Chip
                                    label={t('subscription.pending')}
                                    color="warning"
                                    sx={{position: 'absolute', top: 12, right: 12}}
                                />
                            )}
                            <CardContent>
                                <Typography variant="h4" component="h3" gutterBottom>
                                    {plan.name}
                                </Typography>
                                <Typography variant="h2" component="div" color="primary" gutterBottom>
                                    {plan.price === 0 ? '€0' : `€${plan.price.toFixed(2)}`}
                                </Typography>
                                {plan.price > 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                                        /mese
                                    </Typography>
                                )}
                                {plan.price === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                                        {t('subscription.freeForever')}
                                    </Typography>
                                )}
                                <Divider sx={{my: 2}}/>
                                <Box sx={{textAlign: 'left'}}>
                                    {getFeaturesForPlan(plan.planType).map((feature, index) => (
                                        <Typography key={index} variant="body2" sx={{mb: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                                            <Check fontSize="small" color="success"/>
                                            {feature}
                                        </Typography>
                                    ))}
                                </Box>
                            </CardContent>
                            <CardActions sx={{justifyContent: 'center', mt: 2}}>
                                {isCurrentPlan(plan.planType) ? (
                                    <Button variant="contained" disabled>
                                        {t('subscription.currentPlanButton')}
                                    </Button>
                                ) : (
                                    <Button
                                        variant={isUpgrade(plan.planType) ? 'contained' : 'outlined'}
                                        startIcon={isUpgrade(plan.planType) ? <Upgrade/> : undefined}
                                        disabled
                                    >
                                        {t('subscription.comingSoon')}
                                    </Button>
                                )}
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Cancel Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
                <DialogTitle>{t('subscription.cancelDialogTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('subscription.cancelDialogMessage', {date: formatDate(subscription?.endDate || null)})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>{t('standardUse.undoBtn')}</Button>
                    <Button
                        onClick={handleCancelSubscription}
                        color="error"
                        variant="contained"
                        disabled={actionLoading}
                    >
                        {t('subscription.confirmCancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Downgrade Dialog */}
            <Dialog open={downgradeDialogOpen} onClose={() => setDowngradeDialogOpen(false)}>
                <DialogTitle>{t('subscription.downgradeDialogTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t('subscription.downgradeDialogMessage')}
                    </DialogContentText>
                    {getDowngradeFeatures().length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="error" gutterBottom>
                                {t('subscription.lostFeatures')}:
                            </Typography>
                            {getDowngradeFeatures().map((feature, index) => (
                                <Typography key={index} variant="body2" color="text.secondary">
                                    - {feature}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    <Alert severity="info" sx={{mt: 2}}>
                        {t('subscription.downgradeEffective')}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDowngradeDialogOpen(false)}>{t('standardUse.undoBtn')}</Button>
                    <Button
                        onClick={handleConfirmDowngrade}
                        color="warning"
                        variant="contained"
                        disabled={actionLoading}
                    >
                        {t('subscription.confirmDowngrade')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for success/error messages */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess('')}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert onClose={() => setSuccess('')} severity="success" sx={{width: '100%'}}>
                    {success}
                </Alert>
            </Snackbar>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert onClose={() => setError('')} severity="error" sx={{width: '100%'}}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SubscriptionPlan;
